// src/content/ReporteriaContainer/reporteria-api.js
import request from '../../utils/request';

// Obtener listado de reportes
function getReports(page = 1, limit = 10) {
  return request({
    url: 'reportes',
    method: 'GET',
    private: true,
    params: {
      page,
      limit
    }
  });
}

// Obtener logs de ejecución de reportes
function getReportLogs(page = 1, limit = 10) {
  return request({
    url: 'reportes/logs',
    method: 'GET',
    private: true,
    params: {
      page,
      limit
    }
  });
}

// Obtener un reporte específico
async function getReportById(reportId) {
  try {
    // Primero obtenemos el reporte
    const response = await request({
      url: `reportes/${reportId}`,
      method: 'GET',
      private: true,
    });
    
    if (!response.data || !response.data.reporte) {
      throw new Error('No se recibieron datos del reporte');
    }

    const reportData = response.data.reporte;
    console.log('Datos del reporte recibidos:', reportData);

    // Si el reporte tiene un configId, obtenemos la configuración
    if (reportData.configId) {
      const configResponse = await request({
        url: `reportconfig/${reportData.configId}`,
        method: 'GET',
        private: true,
      });

      if (configResponse.data) {
        // Reemplazamos el configId con la configuración completa
        reportData.configuration = configResponse.data.configuration || configResponse.data;
        // Agregamos los campos name y description si están disponibles
        reportData.name = configResponse.data.name || reportData.name;
        reportData.description = configResponse.data.description || reportData.description;
        console.log('Configuración obtenida:', configResponse.data);
      }
    }

    // Aseguramos que el schedule tenga la estructura correcta
    if (reportData.schedule) {
      const schedule = reportData.schedule;
      console.log('Schedule original:', schedule);

      // Normalizamos la estructura del schedule
      const normalizedSchedule = {
        tipo: schedule.tipo || 'once',
        config: schedule.config || {},
        receptores: schedule.receptores || []
      };

      // Aseguramos que la configuración específica del tipo esté presente
      if (normalizedSchedule.tipo === 'weekly' && !normalizedSchedule.config.diasSemana) {
        normalizedSchedule.config.diasSemana = [];
      } else if (normalizedSchedule.tipo === 'monthly' && !normalizedSchedule.config.diaMes) {
        normalizedSchedule.config.diaMes = 1;
      } else if (normalizedSchedule.tipo === 'once' && !normalizedSchedule.config.fecha) {
        normalizedSchedule.config.fecha = new Date().toISOString().split('T')[0];
      }

      // Aseguramos que la hora esté presente
      if (!normalizedSchedule.config.hora) {
        normalizedSchedule.config.hora = '12:00';
      }

      reportData.schedule = normalizedSchedule;
      console.log('Schedule normalizado:', normalizedSchedule);
    }
    
    return response;
  } catch (error) {
    console.error('Error en getReportById:', error);
    throw error;
  }
}

// Crear nuevo reporte
async function createReport(reportConfig) {
  try {
    // Enviamos name y description dentro de configuration
    const response = await request({
      url: 'reportconfig',
      method: 'POST',
      data: {
        configuration: {
          ...reportConfig.configuration,
          name: reportConfig.name,
          description: reportConfig.description
        },
        schedule: reportConfig.schedule
      },
      private: true,
    });

    return response;
  } catch (error) {
    console.error('Error en createReport:', error);
    console.error('Detalles del error:', error.response?.data);
    console.error('Datos enviados:', reportConfig);
    throw error;
  }
}

// Actualizar reporte
async function updateReport(reportId, updateData) {
  try {
    // Enviamos name y description dentro de configuration
    const updatedReport = await request({
      url: `reportconfig/${reportId}`,
      method: 'PATCH',
      data: {
        configuration: {
          ...updateData.configuration,
          name: updateData.name,
          description: updateData.description
        },
        schedule: updateData.schedule
      },
      private: true,
    });

    return updatedReport;
  } catch (error) {
    console.error('Error en updateReport:', error);
    console.error('Detalles del error:', error.response?.data);
    console.error('Datos enviados:', updateData);
    throw error;
  }
} 

// Eliminar reporte
function deleteReport(reportId) {
  return request({
    url: `reportes/${reportId}`,
    method: 'DELETE',
    private: true,
  });
}

// Descargar reporte
function downloadReport(reportId) {
  return request({
    url: `reportes/${reportId}/descargar`,
    method: 'GET',
    private: true,
    responseType: 'blob', // Importante para descargar archivos
  });
}

// Obtener regiones desde user_extra_data
function getRegions() {
  return request({
    url: 'reportes/filtros/regiones',
    method: 'GET',
    private: true,
  });
}

// Obtener rubros desde company
function getRubros() {
  return request({
    url: 'reportes/filtros/rubros',
    method: 'GET',
    private: true,
  });
}

// Obtener géneros desde user_extra_data
function getGenders() {
  return request({
    url: 'reportes/filtros/generos',
    method: 'GET',
    private: true,
  });
}

// Obtener roles desde la tabla role
async function getRoles() {
  const response = await request({
    url: 'reportes/filtros/roles',
    method: 'GET',
    private: true,
  });
  
  // Filtrar el rol "admin" o "administrador" de la respuesta
  if (response.data && Array.isArray(response.data)) {
    response.data = response.data.filter(role => {
      if (!role.nombre) return true;
      const roleName = role.nombre.toLowerCase();
      return roleName !== 'admin' && roleName !== 'administrador';
    });
  }
  
  return response;
}

// Obtener convocatorias desde la tabla convocatoria
async function getConvocatorias() {
  try {
    const response = await request({
      url: 'reportes/filtros/convocatorias',
      method: 'GET',
      private: true,
    });
    // Filtrar las convocatorias que no tienen un nombre definido
    if (response.data && Array.isArray(response.data)) {
      response.data = response.data.filter(convocatoria => {
        return convocatoria.nombre && convocatoria.nombre.trim() !== '';
      });
    }
    return response;
  } catch (error) {
    console.error('Error al obtener convocatorias:', error);
    return { data: [] }; // Retornar un array vacío en caso de error
  }
}


// Cancelar la programación de un reporte
async function cancelReportSchedule(reportId) {
  try {
    const response = await request({
      url: `reportes/${reportId}/cancel-schedule`,
      method: 'POST',
      private: true,
    });
    return response;
  } catch (error) {
    console.error('Error al cancelar la programación:', error);
    throw error;
  }
}

// Verificar si un reporte puede ser editado
async function canEditReport(reportId) {
  return request({
    url: `reportes/${reportId}/can-edit`,
    method: 'GET',
    private: true,
  });
}

// Validar descarga de reporte
async function validateDownload(logId) {
  return request({
    url: `reportes/logs/${logId}/validate-download`,
    method: 'GET',
    private: true,
  });
}

const ReporteriaAPI = {
  getReports,
  getReportLogs,
  getReportById,
  createReport,
  updateReport,
  deleteReport,
  downloadReport,
  getRegions,
  getRubros,
  getGenders,
  getConvocatorias,
  getRoles,
  cancelReportSchedule,
  canEditReport,
  validateDownload
};

export default ReporteriaAPI;
